import { BarsArrowDownIcon } from "@heroicons/react/24/solid";
import {
    CommunityCardContactMode,
    CommunityId,
    CommunitySortOptions,
    ILocation,
    Localization,
    locationToCoordinates,
    nullEntityId,
    RegionId,
} from "@sp-crm/core";
import { ClientGraphqlError } from "backend/fetcher";
import { ViewMapButton } from "components/shared/view-map-button";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    communitySearchMapMoveEvent,
    communitySearchUpdateSearch,
} from "store/actions/communities";
import { useCommunityCardQuestionIds, useFeature } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import {
    CommunitySearchQuery,
    CommunitySearchRequest,
    useCommunitySearchQuery,
} from "../../generated/graphql";
import { CommunitySearchInstance } from "../../store/reducers/community-search";
import { ResponsiveMode } from "../../store/reducers/responsive";
import { currentLocale } from "../../store/selectors/regions";
import { isGatewayTimeoutError } from "../../util/error-tracking";
import { pluralize } from "../../util/pluralize";
import { Error } from "../error";
import { useDebouncedValue } from "../shared/hooks";
import { ClientCommunityBulkAdd } from "./client-community-bulk-add";
import { CommunityCard, CommunityCardExperience } from "./community-card/community-card";
import { CommunitySearchResultsMap } from "./community-map/community-search-results-map";
import { HoveredMapEntity } from "./community-map/types";
import { CommunitySearchPagination } from "./community-search-pagination";
import { CommunitySearchResultsProps, ListableCommunity } from "./props";
import { CommunitySortOptionDescriptions, SortCommunity } from "./search-bar/sort";
import { StatelessSearchbarItem } from "./search-bar/stateless-searchbar-item";
import { searchRangeMax, searchRangeMin } from "./search-bar/util";
import { ClientCommunityRelationship } from "./types";

const staleTime = 1000 * 60 * 10; // 10m

const searchInstanceToParams = (
    locale: Localization,
    input: CommunitySearchInstance,
    includeLicenses: boolean,
): Omit<CommunitySearchRequest, "questionIds"> => {
    const booleans: string[] = [];
    const nary: string[] = [];
    const rangeFilters: CommunitySearchRequest["rangeFilters"] = [];
    input.advancedAnswers.forEach(a => {
        if (a.booleanAnswer === "true") {
            booleans.push(a.questionId);
        } else if (a.selectionAnswer.length > 0) {
            nary.push(`${a.questionId}:${a.selectionAnswer.join(",")}`);
        } else if (a.rangeAnswer) {
            rangeFilters.push({
                min: isNaN(a.rangeAnswer.min) ? searchRangeMin : a.rangeAnswer.min,
                max: isNaN(a.rangeAnswer.max) ? searchRangeMax : a.rangeAnswer.max,
                questionId: a.questionId,
            });
        }
    });

    const contract: string[] = [];
    if (input.contractActive) contract.push("active");
    if (input.contractNone) contract.push("none");
    if (input.contractTerminated) contract.push("terminated");
    if (input.contractNoState) contract.push("NULL");
    if (input.contractOneOff) contract.push("oneOff");
    if (input.contractPending) contract.push("pending");

    const gender: string[] = [];
    if (input.bothGenders) gender.push("both");
    if (input.maleOnly) gender.push("male");
    if (input.femaleOnly) gender.push("female");
    if (input.genderNotSet) gender.push("NULL");

    const license: string[] = [];
    if (input.licensePending) license.push("pending");
    if (input.licenseActive) license.push("active");
    if (input.licenseOnProbation) license.push("onProbation");
    if (input.licenseClosed) license.push("closed");
    if (input.licenseNotApplicable) license.push("notApplicable");
    if (input.licenseNotSet) license.push("NULL");
    const matchType = input.specificZip
        ? "zip"
        : input.specificCity
          ? "city"
          : input.specificCounty
            ? "county"
            : input.specificState
              ? "state"
              : input.maxDistance &&
                  (input.zip || input.city || input.state || input.address)
                ? "distance"
                : null;

    const bounds: CommunitySearchRequest["bounds"] = input.freeMapCoordinates
        ? {
              northWest: locationToCoordinates(input.freeMapCoordinates.northWest),
              southEast: locationToCoordinates(input.freeMapCoordinates.southEast),
          }
        : null;
    return {
        page: input.clientSidePaginationOnly ? 0 : input.pageNumber,
        perPage: input.clientSidePaginationOnly ? 9999 : input.perPage,
        includeUnrated: input.rating.includeUnrated,
        rating: input.rating.value,
        searchString: input.searchText,
        booleans,
        nary,
        sortKey: input.sort,
        contract: contract.length > 0 ? contract.join(",") : null,
        license: license.length > 0 ? license.join(",") : null,
        priceMax: input.price?.max ?? 9999999,
        priceMin: input.price?.min ?? 0,
        includeNoPrice: input.price?.includePriceUndefined ?? true,
        regionId: input.regionId ?? nullEntityId<RegionId>(),
        zip: input.zip,
        address: input.address,
        city: input.city,
        state: input.state,
        county: matchType === "county" ? input.county : "",
        distance:
            input.maxDistance === undefined
                ? undefined
                : Math.ceil(locale.conversions.toMeters(input.maxDistance)),
        matchType,
        hideDontShow: !!input.hideDontShow,
        matchLowScore: input.matchLowScore,
        matchHighScore: input.matchHighScore,
        matchRule: input.matchRule,
        bedCountMin: input.minCapacity,
        bedCountMax: input.maxCapacity,
        rangeFilters,
        bounds,
        gender,
        includeLicenses,
        availability: input.availability,
    };
};

export const CommunitySearchResults: React.FC<
    CommunitySearchResultsProps & {
        communityCardContactMode: CommunityCardContactMode;
    }
> = props => {
    const responsiveMode: ResponsiveMode = useSelector(
        (appState: ApplicationState) => appState.responsive.mode,
    );
    const [hoveredCommunity, setHoveredCommunity] =
        React.useState<HoveredMapEntity | null>(null);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const liveSearchParams: CommunitySearchInstance = useSelector(
        (appState: ApplicationState) =>
            appState.communitySearch.searches[props.searchKey],
    );
    const [searchInstance] = useDebouncedValue(liveSearchParams, 333, { leading: true });
    const locale = useSelector((appState: ApplicationState) => currentLocale(appState));
    const includeLicenses = useFeature("discreteLicenseRecords");
    const searchParams = React.useMemo(
        () =>
            searchInstance
                ? searchInstanceToParams(locale, searchInstance, includeLicenses)
                : null,
        [locale, searchInstance, includeLicenses],
    );
    const globalDispatch = useDispatch();

    const showMap = responsiveMode >= ResponsiveMode.xLarge;

    const handleMapMoved = React.useCallback(
        (northWest: ILocation, southEast: ILocation) => {
            globalDispatch(
                communitySearchMapMoveEvent(props.searchKey, northWest, southEast),
            );
        },
        [props.searchKey, globalDispatch],
    );

    const handleOpenMap = useCallback(() => {
        setIsMapOpen(true);
    }, []);

    const handleCloseMap = useCallback(() => {
        setIsMapOpen(false);
    }, []);

    if (!searchParams) return null;
    return (
        <div className="pb-20 lg:pb-0">
            <div className="w-full flex community-list collapsible space-x-4">
                <div className="flex-1">
                    <CommunitySearchResultsList
                        {...props}
                        searchParams={searchParams}
                        setHoveredCommunity={setHoveredCommunity}
                        communitySearch={searchInstance}
                        communityCardContactMode={props.communityCardContactMode}
                    />
                </div>
                {showMap ? (
                    <CommunitySearchResultsMap
                        searchParams={searchParams}
                        hoveredCommunity={hoveredCommunity}
                        onMapMoved={handleMapMoved}
                        renderMarkerControl={props.communityCardChildControlGenerator}
                    />
                ) : null}
            </div>
            {!showMap ? <ViewMapButton onClick={handleOpenMap} /> : null}
            <ResponsivePanel
                isOpen={isMapOpen}
                type={PanelType.extraLarge}
                headerText="Map View"
                noPadding={true}
                onDismiss={handleCloseMap}>
                <div className="h-full">
                    <CommunitySearchResultsMap
                        searchParams={searchParams}
                        hoveredCommunity={hoveredCommunity}
                        onMapMoved={handleMapMoved}
                        renderMarkerControl={props.communityCardChildControlGenerator}
                    />
                </div>
            </ResponsivePanel>
        </div>
    );
};

export const CommunitySearchResultsList: React.FC<
    CommunitySearchResultsProps & {
        setHoveredCommunity: (community: HoveredMapEntity | null) => void;
        searchParams: Omit<CommunitySearchRequest, "questionIds">;
        communitySearch: CommunitySearchInstance;
        communityCardContactMode: CommunityCardContactMode;
    }
> = props => {
    const {
        communitySearch,
        clientCommunityRelationshipProvider,
        setHoveredCommunity,
        searchParams,
        placement,
        communityCardContactMode,
    } = props;
    const dispatch = useDispatch();
    const requestedQuestionIds = useCommunityCardQuestionIds();
    const query = useCommunitySearchQuery(
        {
            params: {
                ...searchParams,
                includeQuestionIds: requestedQuestionIds,
            },
        },
        {
            staleTime,
        },
    );
    const [data, setData] = React.useState<CommunitySearchQuery | null>(null);

    const deletedCommunities = data?.communitySearch?.deletedHits ?? [];
    const total = data?.communitySearch?.total ?? 0;
    const [showDeletedCommunities, setShowDeletedCommunities] = useState(false);

    const toggleDeletedCommunities = useCallback(() => {
        setShowDeletedCommunities(prev => !prev);
    }, []);

    React.useEffect(() => {
        if (query && query.data && !query.isError) {
            setData(query.data);
        }
    }, [query, query.data]);
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [searchParams.page]);

    if (query.isLoading && !data) {
        return <Spinner />;
    }

    if (query.error) {
        if (isGatewayTimeoutError(query.error)) {
            return (
                <InlineBanner type="warning">
                    This search did not complete because it took longer than expected.
                    Please reach out to support if this continues.
                </InlineBanner>
            );
        }

        const gqlError = query.error as ClientGraphqlError;
        if (
            typeof gqlError.hasErrorType === "function" &&
            gqlError.hasErrorType("INVALID_RECORD")
        ) {
            return (
                <InlineBanner type="warning">
                    The location data provided does not correspond to a real-world map
                    location. Please review your location search and try again.
                </InlineBanner>
            );
        }

        return (
            <Error componentName="NextgenFilteredCommunityList">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction */}
                {(query.error as any).message}
            </Error>
        );
    }

    if (!data) {
        return <Spinner />;
    }

    let hits = data.communitySearch.hits;

    if (communitySearch.clientSidePaginationOnly) {
        hits = hits.slice(
            communitySearch.pageNumber * communitySearch.perPage,
            communitySearch.pageNumber * communitySearch.perPage +
                communitySearch.perPage,
        );
    }

    const geoReference: ILocation | null = data.communitySearch.coordinates
        ? {
              loc: [
                  parseFloat(data.communitySearch.coordinates.lng),
                  parseFloat(data.communitySearch.coordinates.lat),
              ],
          }
        : null;

    const displayedCommunities = (
        showDeletedCommunities ? [...deletedCommunities, ...hits] : hits
    ) as typeof hits;

    const handleSortChange = (newSort: CommunitySortOptions) => {
        dispatch(
            communitySearchUpdateSearch(props.searchKey, {
                ...communitySearch,
                sort: newSort,
            }),
        );
    };

    const isLocationFilterApplied = !!(
        (communitySearch.county && communitySearch.county.length) ||
        (communitySearch.city && communitySearch.city.length) ||
        (communitySearch.zip && communitySearch.zip.length) ||
        (communitySearch.specificState && communitySearch.state?.length)
    );

    return (
        <>
            {total > 0 ? (
                <div className="lg:flex lg:items-center lg:justify-between lg:space-x-2 max-lg:space-y-4 pb-2 lg:pb-4">
                    <div className={`flex items-center justify-between flex-1`}>
                        <div className="text-sm font-bold flex-1">
                            {total} {pluralize(total, "community", "communities")} found
                        </div>
                        <StatelessSearchbarItem
                            content={
                                <div className="flex items-center space-x-2">
                                    <BarsArrowDownIcon className="w-5 h-5 flex-shrink-0" />
                                    <span className="inline md:inline text-sm">
                                        Sort:{" "}
                                        {CommunitySortOptionDescriptions[
                                            communitySearch.sort
                                        ] ?? "(Name)"}
                                    </span>
                                </div>
                            }>
                            <SortCommunity
                                showDistance={isLocationFilterApplied}
                                value={communitySearch.sort}
                                onChange={handleSortChange}
                            />
                        </StatelessSearchbarItem>
                    </div>
                    {placement ? (
                        <ClientCommunityBulkAdd
                            total={total}
                            searchParams={searchParams}
                            questionIds={requestedQuestionIds}
                            placement={placement}
                        />
                    ) : null}
                </div>
            ) : null}
            {deletedCommunities.length > 0 ? (
                <div className="mb-4">
                    <InlineBanner type="info">
                        {deletedCommunities.length}{" "}
                        {pluralize(
                            deletedCommunities.length,
                            "deleted community matches",
                            "deleted communities match",
                        )}{" "}
                        your search criteria.{" "}
                        <button
                            onClick={toggleDeletedCommunities}
                            className="text-sm font-semibold underline hover:no-underline ml-2">
                            {showDeletedCommunities ? "Hide" : "Show"}
                        </button>
                    </InlineBanner>
                </div>
            ) : null}
            <ListCommunity
                geoReference={geoReference}
                communities={displayedCommunities}
                clientCommunityRelationshipProvider={clientCommunityRelationshipProvider}
                setHoveredCommunity={setHoveredCommunity}
                communityCardContactMode={communityCardContactMode}
            />
            <CommunitySearchPagination
                total={query.data?.communitySearch?.total}
                searchKey={props.searchKey}
            />
        </>
    );
};

interface CommunityRowProps {
    onHover: (community: ListableCommunity) => void;
    onEndHover: () => void;
    clientCommunityRelationshipProvider?: (
        communityId: CommunityId,
    ) => ClientCommunityRelationship;
    community: ListableCommunity;
    geoReference: ILocation;
    communityCardContactMode: CommunityCardContactMode;
}

const CommunityRow: React.FC<CommunityRowProps> = props => {
    const {
        community,
        clientCommunityRelationshipProvider,
        geoReference,
        onHover,
        onEndHover,
        communityCardContactMode,
    } = props;

    const handleMouseEnter = React.useCallback(() => {
        onHover(community);
    }, [community, onHover]);

    const handleMouseLeave = React.useCallback(() => {
        onEndHover();
    }, [onEndHover]);

    return (
        <li key={community.id}>
            <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {clientCommunityRelationshipProvider ? (
                    <CommunityCard
                        clientCommunityRelationship={clientCommunityRelationshipProvider(
                            community.id,
                        )}
                        community={community}
                        experience={CommunityCardExperience.ClientMatchSearch}
                        geoReference={geoReference}
                        communityCardContactMode={communityCardContactMode}
                    />
                ) : (
                    <CommunityCard
                        community={community}
                        experience={CommunityCardExperience.GlobalSearch}
                        geoReference={geoReference}
                        communityCardContactMode={communityCardContactMode}
                    />
                )}
            </span>
        </li>
    );
};

interface ListCommunityProps {
    communities: ListableCommunity[];
    geoReference: ILocation;
    clientCommunityRelationshipProvider?: (
        communityId: CommunityId,
    ) => ClientCommunityRelationship;
    communityCardContactMode: CommunityCardContactMode;
    setHoveredCommunity: (community: HoveredMapEntity | null) => void;
}

export const ListCommunity: React.FC<ListCommunityProps> = props => {
    const {
        geoReference,
        clientCommunityRelationshipProvider,
        communities,
        setHoveredCommunity,
        communityCardContactMode,
    } = props;

    const handleHover = React.useCallback(
        (community: ListableCommunity) => {
            const location =
                community.lat && community.lng
                    ? { lat: parseFloat(community.lat), lng: parseFloat(community.lng) }
                    : undefined;
            setHoveredCommunity({ entityId: community.id, location });
        },
        [setHoveredCommunity],
    );

    const handleEndHover = React.useCallback(() => {
        setHoveredCommunity(null);
    }, [setHoveredCommunity]);

    const communityRows = useMemo(() => {
        return (communities || []).map(c => (
            <CommunityRow
                key={c.id}
                community={c}
                geoReference={geoReference}
                clientCommunityRelationshipProvider={clientCommunityRelationshipProvider}
                onHover={handleHover}
                onEndHover={handleEndHover}
                communityCardContactMode={communityCardContactMode}
            />
        ));
    }, [
        communities,
        geoReference,
        clientCommunityRelationshipProvider,
        handleHover,
        handleEndHover,
        communityCardContactMode,
    ]);

    return <ul className="space-y-2 lg:space-y-4">{communityRows}</ul>;
};
